/**
 * Datadog Real User Monitoring (RUM) Client
 *
 * Provides a shared initialization function for Datadog RUM across apps.
 * Each app provides its own page-filtering logic and React integration;
 * this module owns the init configuration and idempotency guard.
 *
 * @see https://docs.datadoghq.com/real_user_monitoring/
 */

import { datadogRum } from "@datadog/browser-rum";
import { getConfig } from "./config";

export interface RumConfig {
  /** Override session sample rate (default: 100) */
  sessionSampleRate?: number;
  /** Override session replay sample rate (default: 0) */
  sessionReplaySampleRate?: number;
  /** Track user interactions like clicks (default: true) */
  trackUserInteractions?: boolean;
  /** Track resource loading times (default: true) */
  trackResources?: boolean;
}

/**
 * Initializes Datadog RUM if not already initialized.
 *
 * Reads application ID and client token from environment variables:
 * - NEXT_PUBLIC_DATADOG_APPLICATION_ID
 * - NEXT_PUBLIC_DATADOG_CLIENT_TOKEN
 *
 * Service name is resolved from the shared Datadog config (see `configureDatadog`
 * or NEXT_PUBLIC_APP_NAME env var).
 *
 * Returns true if RUM was initialized (or was already initialized), false if
 * required config is missing.
 */
export function initDatadogRum(config: RumConfig = {}): boolean {
  if (typeof window === "undefined") return false;
  if (datadogRum.getInitConfiguration()) return true;

  // Skip RUM for Datadog Synthetic browser tests to keep session data clean.
  if (
    (window as Window & { _DATADOG_SYNTHETICS_BROWSER?: unknown })
      ._DATADOG_SYNTHETICS_BROWSER
  ) {
    return false;
  }

  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
  if (!applicationId || !clientToken) return false;

  let appName: string;
  try {
    ({ appName } = getConfig());
  } catch {
    return false;
  }

  datadogRum.init({
    applicationId,
    clientToken,
    site: "datadoghq.com",
    service: appName,
    sessionSampleRate: config.sessionSampleRate ?? 100,
    sessionReplaySampleRate: config.sessionReplaySampleRate ?? 0,
    trackUserInteractions: config.trackUserInteractions ?? true,
    trackResources: config.trackResources ?? true,
  });

  return true;
}
